<template>
    <!-- Raiz <div> y nunca <fragment>: vue-fragment parchea
         Node.prototype.insertBefore y reubica los hijos del fragmento dentro
         del padre. Eso dejaba obsoletas las referencias de DOM que cacheaban
         los editores de terceros que se probaron antes acá, y era la causa del
         cursor congelado. Ahora el cursor es el nativo del <textarea> y nada
         reconcilia DOM encima. -->
    <div>
        <div v-if="enabled">
            <div class="flex flex-wrap items-center gap-1">
                <app-button
                    v-for="block in blockItems"
                    :key="block.key"
                    type="button"
                    size="small"
                    relief
                    :title="block.detail"
                    @click="onBlockClick(block)"
                >
                    {{ block.label }}
                </app-button>
                <app-button
                    type="button"
                    size="small"
                    flat
                    class="ml-auto"
                    @click="isPreviewVisible = !isPreviewVisible"
                >
                    {{
                        isPreviewVisible
                            ? "Ocultar vista previa"
                            : "Ver vista previa"
                    }}
                </app-button>
            </div>

            <p class="mt-1 text-xs text-theme-muted">
                Escribe / al inicio de una línea para insertar un bloque
            </p>

            <div
                class="mt-2 grid grid-cols-1 gap-3"
                :class="isPreviewVisible ? 'lg:grid-cols-2' : ''"
            >
                <div class="relative">
                    <textarea
                        ref="input"
                        v-model="item"
                        class="w-full min-h-72 resize-y rounded-lg border border-theme-border bg-theme-input px-3 py-2 font-mono text-sm text-theme-base focus:outline-none focus:ring-1 focus:ring-theme-primary"
                        placeholder="Escribe el contenido con Markdown"
                        @input="onInput"
                        @keydown="onKeyDown"
                        @keyup="syncSlashMenu"
                        @click="syncSlashMenu"
                        @scroll="updateSlashPosition"
                        @blur="closeSlashMenu"
                    ></textarea>

                    <ul
                        v-if="slash.isOpen"
                        ref="slashMenu"
                        class="absolute z-600 w-56 max-h-slash-menu overflow-y-auto rounded-lg border border-theme-border bg-theme-card py-1 shadow-lg"
                        :class="slashMenuStyle ? '' : 'left-0 top-full mt-1'"
                        :style="slashMenuStyle"
                    >
                        <li
                            v-for="(block, index) in slash.items"
                            :key="block.key"
                        >
                            <button
                                type="button"
                                class="flex w-full flex-col items-start px-3 py-1 text-left hover:bg-theme-hover"
                                :class="
                                    index === slash.activeIndex
                                        ? 'bg-theme-hover'
                                        : ''
                                "
                                @mousedown.prevent="chooseSlashItem(index)"
                            >
                                <span class="text-sm text-theme-base">
                                    {{ block.label }}
                                </span>
                                <span class="text-2.5 text-theme-muted">
                                    {{ block.detail }}
                                </span>
                            </button>
                        </li>
                    </ul>
                </div>

                <div v-if="isPreviewVisible">
                    <span class="text-xs text-theme-muted">Vista previa</span>
                    <div
                        class="markdown-body mt-1 min-h-72 overflow-x-auto rounded-lg border border-theme-border bg-theme-card px-3 py-2"
                        v-html="previewHtml"
                    ></div>
                </div>
            </div>
        </div>

        <div v-else class="markdown-body" v-html="previewHtml"></div>

        <image-selector
            v-if="enabled"
            v-model="isImageSelectorOpen"
            enabled-create
            message="Puedes seleccionar una imagen de cualquier tamaño"
            @input="onSelectImage"
        />

        <app-dialog
            v-if="enabled"
            v-model="isBlockFormOpen"
            size="sm"
            append-to-body
            @close="onBlockFormClose"
        >
            <template #header>
                <h4 class="text-lg font-semibold">{{ blockFormTitle }}</h4>
            </template>
            <div class="flex flex-col gap-3">
                <custom-input
                    v-if="blockForm.type === 'cta'"
                    v-model="blockForm.label"
                    label="Texto del botón"
                    placeholder="Comprar ahora"
                />
                <custom-input
                    v-model="blockForm.url"
                    label="Enlace"
                    placeholder="https://"
                />
                <p v-if="blockFormError" class="text-xs text-theme-danger">
                    {{ blockFormError }}
                </p>
            </div>
            <template #footer>
                <div class="flex justify-end">
                    <app-button type="button" @click="submitBlockForm">
                        Guardar bloque
                    </app-button>
                </div>
            </template>
        </app-dialog>

        <image-block-dialog
            v-if="enabled"
            v-model="isImageFormOpen"
            :image="imageDraft"
            @submit="insertImageBlock"
        />

        <table-editor-dialog
            v-if="enabled"
            v-model="isTableFormOpen"
            :table="tableDraft"
            :editing="isEditingTable"
            @submit="insertTable"
        />
    </div>
</template>

<script>
import { renderMarkdown } from "@/common/markdown/renderMarkdown";

import AppButton from "@/components/utils/AppButton.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImageSelector from "@/components/utils/ImageSelector.vue";

import ImageBlockDialog from "./markdown-editor/ImageBlockDialog.vue";
import TableEditorDialog from "./markdown-editor/TableEditorDialog.vue";
import {
    EDITOR_BLOCKS,
    URL_PATTERN,
    buildCtaSnippet,
    buildEmbedSnippet,
    filterBlocks
} from "./markdown-editor/blocks";
import { getCaretCoordinates } from "./markdown-editor/caret";
import { insertSnippet, readSlashQuery } from "./markdown-editor/editing";
import { buildImageSnippet } from "./markdown-editor/images";
import {
    createEmptyTable,
    findTableAt,
    serializeTable
} from "./markdown-editor/tables";

const PREVIEW_DELAY = 200;

const SLASH_MENU_WIDTH = 224;

const SLASH_MENU_GAP = 4;

const BLOCK_FORM_TITLES = {
    cta: "Botón de llamada a la acción",
    embed: "Video incrustado"
};

const CLOSED_SLASH = {
    isOpen: false,
    start: 0,
    end: 0,
    query: "",
    items: [],
    activeIndex: 0
};

export default {
    name: "MarkdownEditor",
    components: {
        AppButton,
        AppDialog,
        CustomInput,
        ImageBlockDialog,
        ImageSelector,
        TableEditorDialog
    },
    model: {
        prop: "itemCurrent",
        event: "change"
    },
    props: {
        itemCurrent: {
            default: () => "",
            type: String,
            require: true
        },
        enabled: {
            default: () => true,
            type: Boolean,
            required: false
        }
    },
    emits: ["change"],
    data: () => ({
        item: "",
        previewSource: "",
        isPreviewVisible: true,
        isImageSelectorOpen: false,
        isImageFormOpen: false,
        imageDraft: null,
        isTableFormOpen: false,
        isEditingTable: false,
        tableDraft: null,
        tableRange: null,
        isBlockFormOpen: false,
        blockForm: { type: "", label: "", url: "" },
        blockFormError: "",
        promptRange: null,
        slash: { ...CLOSED_SLASH },
        slashMenuStyle: null,
        isSlashDismissed: false
    }),
    computed: {
        blockItems() {
            return EDITOR_BLOCKS;
        },
        previewHtml() {
            return renderMarkdown(this.previewSource);
        },
        blockFormTitle() {
            return BLOCK_FORM_TITLES[this.blockForm.type] || "Bloque";
        }
    },
    watch: {
        // Las pantallas que lo usan cargan el contenido despues de montar: si
        // el cursor sigue en 0 (nadie tocó el textarea) hay que dejarlo al
        // final del texto que acaba de llegar, o el primer bloque que inserten
        // desde la barra entraria antes de todo lo escrito.
        itemCurrent(value) {
            const nextValue = value || "";
            if (nextValue === this.item) return;
            this.item = nextValue;
            this.previewSource = nextValue;
            this.$nextTick(() => {
                if (document.activeElement === this.$refs.input) return;
                this.moveCaretToEnd();
            });
        },
        enabled(value) {
            if (!value) {
                this.closeSlashMenu();
                return;
            }
            this.$nextTick(() => this.moveCaretToEnd());
        },
        // El menú se ancla al caret con posición fija, así que cualquier scroll
        // de la página lo dejaría flotando lejos del cursor.
        "slash.isOpen"(isOpen) {
            if (isOpen) {
                window.addEventListener("resize", this.updateSlashPosition);
                window.addEventListener(
                    "scroll",
                    this.updateSlashPosition,
                    true
                );
                return;
            }
            this.slashMenuStyle = null;
            this.stopTrackingSlashPosition();
        }
    },
    created() {
        this.previewTimer = null;
        this.item = this.itemCurrent || "";
        this.previewSource = this.item;
    },
    mounted() {
        this.moveCaretToEnd();
    },
    beforeDestroy() {
        if (this.previewTimer) clearTimeout(this.previewTimer);
        this.stopTrackingSlashPosition();
    },
    methods: {
        onInput(event) {
            this.item = event.target.value;
            this.$emit("change", this.item);
            this.schedulePreview();
            this.syncSlashMenu();
        },
        schedulePreview() {
            if (this.previewTimer) clearTimeout(this.previewTimer);
            this.previewTimer = setTimeout(() => {
                this.previewTimer = null;
                this.previewSource = this.item;
            }, PREVIEW_DELAY);
        },
        moveCaretToEnd() {
            const input = this.$refs.input;
            if (!input) return;
            input.setSelectionRange(this.item.length, this.item.length);
        },
        // Si quedó un "/algo" a medio escribir, el bloque lo reemplaza: esa
        // linea es un comando, no contenido, sea que el bloque se elija del
        // menú o de la barra de botones.
        currentRange() {
            const input = this.$refs.input;
            if (!input)
                return { start: this.item.length, end: this.item.length };
            const pending = readSlashQuery(input.value, input.selectionEnd);
            if (pending) return { start: pending.start, end: pending.end };
            return { start: input.selectionEnd, end: input.selectionEnd };
        },
        onBlockClick(block) {
            this.applyBlock(block, null);
        },
        applyBlock(block, range) {
            const target = range || this.currentRange();
            if (block.prompt) {
                this.openPrompt(block.prompt, target);
                return;
            }
            this.applySnippet(block.snippet, target);
        },
        applySnippet(snippet, range) {
            const result = insertSnippet(this.item, range, snippet);
            if (this.previewTimer) clearTimeout(this.previewTimer);
            this.previewTimer = null;
            this.item = result.value;
            this.previewSource = result.value;
            this.$emit("change", result.value);
            this.$nextTick(() => {
                const input = this.$refs.input;
                if (!input) return;
                input.focus();
                input.setSelectionRange(
                    result.selectionStart,
                    result.selectionEnd
                );
            });
        },
        openPrompt(prompt, range) {
            this.promptRange = range;
            if (prompt === "image") {
                this.isImageSelectorOpen = true;
                return;
            }
            if (prompt === "table") {
                this.openTableForm(range);
                return;
            }
            this.blockForm = { type: prompt, label: "", url: "" };
            this.blockFormError = "";
            this.isBlockFormOpen = true;
        },
        // Con el cursor dentro de una tabla el bloque "Tabla" no inserta otra:
        // abre la que ya está para editarla, así el tenant nunca tiene que
        // cuadrar los | ni la fila de guiones a mano.
        openTableForm(range) {
            const input = this.$refs.input;
            const caret = input ? input.selectionEnd : this.item.length;
            const current = findTableAt(this.item, caret);

            this.isEditingTable = Boolean(current);
            this.tableRange = current
                ? { start: current.start, end: current.end }
                : range;
            this.tableDraft = current
                ? { alignments: current.alignments, rows: current.rows }
                : createEmptyTable();
            this.isTableFormOpen = true;
        },
        insertTable(table) {
            this.isTableFormOpen = false;
            this.applySnippet(
                { text: serializeTable(table), block: true },
                this.tableRange || this.currentRange()
            );
        },
        onBlockFormClose() {
            this.blockFormError = "";
        },
        submitBlockForm() {
            const { label, type, url } = this.blockForm;
            const cleanUrl = url.trim();
            const cleanLabel = label.trim();

            if (!URL_PATTERN.test(cleanUrl)) {
                this.blockFormError =
                    "Escribe un enlace completo, que empiece por http:// o https://";
                return;
            }
            if (type === "cta" && !cleanLabel) {
                this.blockFormError = "Escribe el texto del botón";
                return;
            }
            // `renderMarkdown()` parte la linea del cta por "|": un texto con
            // "|" publicaria un boton con el enlace roto.
            if (type === "cta" && cleanLabel.includes("|")) {
                this.blockFormError =
                    "El texto del botón no puede llevar el carácter |";
                return;
            }

            const snippet =
                type === "cta"
                    ? buildCtaSnippet({ label: cleanLabel, url: cleanUrl })
                    : buildEmbedSnippet({ url: cleanUrl });

            this.isBlockFormOpen = false;
            this.applySnippet(snippet, this.promptRange || this.currentRange());
        },
        // La imagen no se inserta apenas se elige: antes se pregunta tamaño,
        // posición y enlace, que es lo que markdown no sabe expresar.
        onSelectImage({ alt, url }) {
            if (!url) return;
            this.imageDraft = { alt: alt || "", url };
            this.isImageFormOpen = true;
        },
        insertImageBlock(options) {
            this.isImageFormOpen = false;
            this.applySnippet(
                buildImageSnippet(options),
                this.promptRange || this.currentRange()
            );
        },
        syncSlashMenu() {
            const input = this.$refs.input;
            if (!input) return;
            const found = readSlashQuery(input.value, input.selectionStart);
            if (!found) {
                this.isSlashDismissed = false;
                this.closeSlashMenu();
                return;
            }
            if (this.isSlashDismissed) return;

            const items = filterBlocks(found.query);
            if (!items.length) {
                this.closeSlashMenu();
                return;
            }

            const keepsSelection =
                this.slash.isOpen &&
                this.slash.start === found.start &&
                this.slash.query === found.query;

            this.slash = {
                isOpen: true,
                start: found.start,
                end: found.end,
                query: found.query,
                items,
                activeIndex: keepsSelection
                    ? Math.min(this.slash.activeIndex, items.length - 1)
                    : 0
            };
            this.$nextTick(this.updateSlashPosition);
        },
        // El popup se ancla al caret. Si la medición no se puede hacer (el
        // <textarea> no expone la posición del cursor, se calcula con un div
        // espejo) queda el estilo en null y el menú cae a su posición por
        // defecto, colgado del borde inferior del editor.
        updateSlashPosition() {
            const input = this.$refs.input;
            if (!this.slash.isOpen || !input) return;

            const caret = getCaretCoordinates(input, this.slash.end);
            if (!caret) {
                this.slashMenuStyle = null;
                return;
            }

            const rect = input.getBoundingClientRect();
            const caretTop = rect.top + caret.top - input.scrollTop;
            const caretLeft = rect.left + caret.left - input.scrollLeft;
            const menu = this.$refs.slashMenu;
            const menuHeight = menu ? menu.offsetHeight : 0;
            const spaceBelow =
                window.innerHeight - (caretTop + caret.height + SLASH_MENU_GAP);
            const opensUpward =
                spaceBelow < menuHeight && caretTop > spaceBelow;

            this.slashMenuStyle = {
                position: "fixed",
                left: `${Math.max(
                    rect.left,
                    Math.min(caretLeft, rect.right - SLASH_MENU_WIDTH)
                )}px`,
                ...(opensUpward
                    ? {
                          bottom: `${
                              window.innerHeight - caretTop + SLASH_MENU_GAP
                          }px`
                      }
                    : {
                          top: `${caretTop + caret.height + SLASH_MENU_GAP}px`
                      })
            };
        },
        stopTrackingSlashPosition() {
            window.removeEventListener("resize", this.updateSlashPosition);
            window.removeEventListener(
                "scroll",
                this.updateSlashPosition,
                true
            );
        },
        closeSlashMenu() {
            if (!this.slash.isOpen) return;
            this.slash = { ...CLOSED_SLASH };
        },
        dismissSlashMenu() {
            this.isSlashDismissed = true;
            this.closeSlashMenu();
        },
        moveSlashSelection(offset) {
            const total = this.slash.items.length;
            if (!total) return;
            this.slash.activeIndex =
                (this.slash.activeIndex + offset + total) % total;
        },
        chooseSlashItem(index) {
            const block = this.slash.items[index];
            if (!block) return;
            const range = { start: this.slash.start, end: this.slash.end };
            this.closeSlashMenu();
            this.applyBlock(block, range);
        },
        onKeyDown(event) {
            if (!this.slash.isOpen) return;
            if (event.key === "ArrowDown") {
                event.preventDefault();
                this.moveSlashSelection(1);
                return;
            }
            if (event.key === "ArrowUp") {
                event.preventDefault();
                this.moveSlashSelection(-1);
                return;
            }
            if (event.key === "Enter" || event.key === "Tab") {
                event.preventDefault();
                this.chooseSlashItem(this.slash.activeIndex);
                return;
            }
            if (event.key === "Escape") {
                event.preventDefault();
                this.dismissSlashMenu();
            }
        }
    }
};
</script>
